import {
  CcfAgentLegConnectedIcon,
  CcfAgentLegDialingIcon,
  CcfAgentLegDisconnectedIcon,
  CcfBox,
  CcfLoader,
  CcfTooltip,
  useTranslator,
} from '@nice-ccf/ui-controls';
import agentLegStyle from './ccf-agent-leg.styles';
import { Box, IconButton, SvgIcon, useTheme } from '@mui/material';
import { useEffect, useState } from 'react';
import { AgentLegStatus } from '@nice-ccf/common-sdk';
import ccfAppHeaderStyles from '../ccf-app-header/ccf-app-header-styles';
import { useSelector } from 'react-redux';
import { activeContactSelector } from '../ccf-inbox/ccf-inbox.slice';
import { DigitalChannelStatus, VoiceContactStatus } from '@nice-ccf/core-sdk';
import styles from './ccf-agent-leg.module.scss';
import { getEnableLyraDesign } from '../global.app.slice';

/* eslint-disable-next-line */
export interface CcfAgentLegProps {}

/**
 * Component displays an icon with the different agent leg status
 * @returns tooltip with different agent leg status
 * @example <CcfAgentLeg/>
 */
export function CcfAgentLeg(props: CcfAgentLegProps) {
  const [translate] = useTranslator();
  const theme = useTheme();
  const agentLegStyles = agentLegStyle(theme);
  const appHeaderStyles = ccfAppHeaderStyles(theme);
  const activeContact = useSelector(activeContactSelector);
  const enableLyraDesign = useSelector(getEnableLyraDesign);
  const [agentLegConnectedStatus, setAgentLegConnectedStatus] = useState(
    AgentLegStatus.DISCONNECTED
  );

  const lyraFill = 'rgb(91,103,112)';
  const defaultFill = '#ffffff';
  const fill = enableLyraDesign ? lyraFill : defaultFill;

  const LyraUnlinkIcon = (extraProps?: any) => (
    <SvgIcon viewBox="0 0 24 24" {...extraProps}>
      <path
        d="M15 7h2a5 5 0 0 1 0 10h-2m-6 0H7A5 5 0 0 1 7 7h2"
        fill="none"
        stroke={lyraFill}
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </SvgIcon>
  );

  const LyraUnlinkConnectedIcon = (extraProps?: any) => (
    <SvgIcon viewBox="0 0 24 24" {...extraProps}>
      <path
        d="M15 7h2a5 5 0 0 1 0 10h-2m-6 0H7A5 5 0 0 1 7 7h2"
        fill="none"
        stroke={lyraFill}
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <line
        x1="8" y1="12" x2="16" y2="12"
        fill="none"
        stroke={lyraFill}
        strokeWidth="1.75"
        strokeLinecap="round"
      />
    </SvgIcon>
  );

  const agentLegIconMap = enableLyraDesign
    ? new Map([
        [
          AgentLegStatus.DISCONNECTED.toString(),
          <LyraUnlinkIcon data-testid="disconnected" />,
        ],
        [
          AgentLegStatus.DIALING.toString(),
          <>
            <LyraUnlinkIcon style={{ marginBottom: '-8px', marginLeft: '7px' }} />
            <CcfLoader
              showLoadingText={false}
              isPrimary={false}
              brandingColor={lyraFill}
            />
          </>,
        ],
        [
          AgentLegStatus.ACTIVE.toString(),
          <LyraUnlinkConnectedIcon />,
        ],
      ])
    : new Map([
        [
          AgentLegStatus.DISCONNECTED.toString(),
          <CcfAgentLegDisconnectedIcon
            data-testid="disconnected"
            fill={defaultFill}
          />,
        ],
        [
          AgentLegStatus.DIALING.toString(),
          <>
            <CcfAgentLegDialingIcon
              fill={defaultFill}
              style={{ marginBottom: '-8px', marginLeft: '7px' }}
            />
            <CcfLoader
              showLoadingText={false}
              isPrimary={false}
              brandingColor={defaultFill}
            />
          </>,
        ],
        [
          AgentLegStatus.ACTIVE.toString(),
          <CcfAgentLegConnectedIcon fill={defaultFill} />,
        ],
      ]);

  /**
   * Function to handle different agent leg like - connect or disconnect
   * @param e - React.SyntheticEvent
   * @example updateAgentLeg(e)
   */
  const updateAgentLeg = (e: React.SyntheticEvent) => {
    setAgentLegConnectedStatus(AgentLegStatus.DIALING);
    e.preventDefault();
    setTimeout(() => {
      if (agentLegConnectedStatus === AgentLegStatus.ACTIVE) {
        setAgentLegConnectedStatus(AgentLegStatus.DISCONNECTED);
      } else if (agentLegConnectedStatus === AgentLegStatus.DISCONNECTED) {
        setAgentLegConnectedStatus(AgentLegStatus.ACTIVE);
      }
    }, 3000);
  };

  useEffect(() => {
    if (
      activeContact?.contactStatus === DigitalChannelStatus.OPEN ||
      activeContact?.contactStatus === VoiceContactStatus.ACTIVE
    ) {
      if (agentLegConnectedStatus === AgentLegStatus.DISCONNECTED) {
        setAgentLegConnectedStatus(AgentLegStatus.DIALING);
        setTimeout(() => {
          setAgentLegConnectedStatus(AgentLegStatus.ACTIVE);
        }, 3000);
      }
    }
  }, [activeContact?.contactStatus]);

  /**
   * Function to give translated text of agentLegStatus
   * @param agentLegStatus - agentLegStatusValue
   * @example getAgentLegStatusTranslated('Active')
   */
  const getAgentLegStatusTranslated = (agentLegStatus: string) => {
    switch (agentLegStatus) {
      case AgentLegStatus.DIALING:
        return translate('agentLegDialing');
      case AgentLegStatus.ACTIVE:
        return (
          <>
            <div>{translate('agentLegConnected')}</div>
            <div>{translate('clickToDisconnect')}</div>
          </>
        );
      default:
        return (
          <>
            <div>{translate('agentLegDisconnected')}</div>
            <div>{translate('clickToConnect')}</div>
          </>
        );
    }
  };

  return (
    <CcfTooltip
      title={<Box>{getAgentLegStatusTranslated(agentLegConnectedStatus)}</Box>}
      arrow
      tooltipForRTL="ccfTooltipRight0"
      aria-label={translate('agentLeg')}
    >
      <IconButton
        color="inherit"
        sx={enableLyraDesign ? undefined : [appHeaderStyles.button, agentLegStyles.agentLeg]}
        disableFocusRipple={true}
        role="button"
        onClick={updateAgentLeg}
        data-testid="agent-leg-status"
        className={enableLyraDesign ? styles.agentLegLyra : styles.agentLeg}
      >
        {agentLegIconMap.get(agentLegConnectedStatus)}
      </IconButton>
    </CcfTooltip>
  );
}

export default CcfAgentLeg;
